import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import ScrollBasic from '../components/shared/ScrollBasic';
import { FriendTile } from '../components/FriendTile';
import { getRandomShareInviteCreative } from '../../creatives/share';
import { inviteAsync } from '../../lib/sharing';
import { getCreativeText } from '../../creatives/text';
import { Feature } from 'src/replicant/analytics';
import { openPopupPromise } from '../../lib/popups/popupOpenClose';
import StateObserver from '../../StateObserver';
import { ImageView } from '@play-co/timestep-core/ui';
import { Social } from 'src/replicant/social';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { TextInput } from '../components/shared/TextInput';
import { getLocale } from 'src/replicant/getters/user';
import { SocialSubFeature } from 'src/replicant/social/social.ruleset';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import Checkbox from '../components/shared/Checkbox';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { captureMessage } from '@sentry/browser';

type SocialFriend = {
  id: string;
  name: string;
  photo: string;
};
type CloseHandler = (result?: string[]) => void;

const inputStyle = `
  input.popupInviteFriends {
    background: #BF8147 !important;
    box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    color: #F1F5EF;
    font-weight: bold;
    size: 10vm;
    padding: 0 1em !important;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.7);
  }
  input.popupInviteFriends::placeholder {
    color: #c1c4bf;
  }
`;

export default class PopupInviteFriends extends PopupBasic {
  private friends: SocialFriend[];
  private friendsList: ScrollBasic<SocialFriend>;
  private buttonCancel: ButtonScaleViewWithText;
  private subFeature: SocialSubFeature;

  // Used for TEST_FRIEND_SEARCH A/B test
  private style: HTMLStyleElement;

  // Used for TEST_INVITE_MULTI_SELECT
  private selectedFriends: Map<SocialFriend, boolean> = new Map();
  private selectionCounter: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 915,
      darkerBg: false,
      tracking: { feature: 'popup', $subFeature: 'popup-invitefriends' },
      titleIcon: { image: 'assets/ui/buruburu/friend_referral_icon.png' },
    });

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      size: 28,
      width: 440,
      height: 94,
      x: 102,
      y: 74,
      zIndex: 1,
    });

    const friendsListRect = {
      x: 30,
      y: 170,
      width: this.box.style.width - 60,
      height: 600,
    };
    // eslint-disable-next-line no-constant-condition
    if (false) {
      // Attach style element to enable placeholder styling via pseudo-element
      this.style = document.createElement('style');
      this.style.appendChild(document.createTextNode(inputStyle));
      document.head.appendChild(this.style);

      const inputHeight = 60;
      const searchInput = this.box.addSubview(
        new TextInput({
          ...friendsListRect,
          inputClass: 'popupInviteFriends',
          height: inputHeight,
          placeholder: i18n('social.invite.referral.search'),
        }),
      );
      searchInput.on('InputValidated', () => {
        this.filterFriends(searchInput.input.value);
      });

      friendsListRect.y += inputHeight;
      friendsListRect.height -= inputHeight;
    }

    this.friendsList = new ScrollBasic<SocialFriend>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      rect: friendsListRect,
      firstItemOffset: 10,
    });

    this.buttonCancel = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: -1,
      fontSize: 46,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: this.box.style.height - 25 - 100 / 2,
      width: 259,
      height: 100,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return this.close();
      },
      tracking: { feature: 'popup', $subFeature: 'popup-invitefriends-close' },
    });

    if (this.isMultiSelectEnabled()) {
      const containerWidth = this.box.style.width;
      this.buttonCancel.updateOpts({ x: containerWidth * 0.265 });

      const { y, width, height } = this.buttonCancel.style;
      const inviteButton = this.box.addSubview(
        new ButtonScaleViewWithText({
          ...uiConfig.buttons.secondary,
          labelOffsetY: -13,
          fontSize: 46,
          font: bitmapFonts('Body'),
          centerOnOrigin: true,
          x: containerWidth * 0.735,
          y,
          width,
          height,
          localeText: () => i18n('social.invite.referral.cta'),
          onClick: async () => {
            const selectedFriends = this.getSelectedFriends();
            if (!selectedFriends.length) {
              return;
            }

            sounds.sfx.play('ok');
            await this.confirmInvite(...selectedFriends);
          },
          tracking: {
            feature: 'popup',
            $subFeature: 'popup-invitefriends-invite',
          },
        }),
      );

      this.selectionCounter = inviteButton.addSubview(
        new LangBitmapFontTextView({
          y: inviteButton.style.height / 2,
          width: inviteButton.style.width,
          height: inviteButton.style.height / 2,
          font: 'Body',
          size: 26,
          color: '#017C58',
          align: 'center',
          verticalAlign: 'center',
        }),
      );
    }
  }

  init(opts: { friends: SocialFriend[]; subFeature: SocialSubFeature }) {
    this.subFeature = opts.subFeature;

    const reward = Social.getters(
      StateObserver.getState().user,
    ).getInviteReward(opts.subFeature);

    super.init({
      title: i18n('social.invite.referral.title'),
      message: this.getRewardMessage(reward.type, reward.value),
    });

    this.friends = [...opts.friends];
    this.friendsList.setItems(
      opts.friends.slice(0, Social.ruleset.inviteFriendListLimit),
    );

    // Add reward icon for message
    this.message.removeAllSubviews();
    this.message.addSubview(this.getRewardIcon(reward.type));

    // update selection counter based on subFeature
    this.updateSelectionCounter(0);
  }

  /**
   * Depends on reward type return different messages for gems anf for energy
   */
  private getRewardMessage(type: 'gems' | 'energy', value: number) {
    switch (type) {
      case 'gems':
        return i18n('social.invite.referral.message.gems', { value });
      case 'energy':
        return i18n('social.invite.referral.message.energy', { value });
    }
  }

  /**
   * Get reward icon for message depend on reward type
   */
  private getRewardIcon(type: 'gems' | 'energy') {
    switch (type) {
      case 'gems':
        return new ImageView({
          image: 'assets/ui/hud/icons/hud_gem.png',
          x: -70,
          y: 16,
          width: 50,
          height: 42,
          scale: 1.5,
        });
      case 'energy':
        return new ImageView({
          image: 'assets/ui/hud/icons/hud_energy.png',
          x: -70,
          y: 16,
          width: 39,
          height: 46,
          scale: 1.5,
        });
    }
  }

  private close() {
    this.style?.remove();
    this.opts.close();
  }

  private isMultiSelectEnabled() {
    return false;
  }

  private createItem(superview: View, _, friend: SocialFriend) {
    const container = superview.addSubview(
      new FlexBoxView({
        width: superview.style.width - 10,
        height: uiConfig.buruburu.invite.friendTile.container.height - 6,
        flex: {
          alignItems: 'center',
        },
      }),
    );

    let onClick: () => Promise<void>;
    const { user } = StateObserver.getState();
    if (this.isMultiSelectEnabled()) {
      const checkbox = new Checkbox({
        checked: this.selectedFriends.get(friend),
        onToggle: (checked) => {
          const selectionCount = this.getSelectedFriends().length;
          const limit = Social.getters(user).getInviteLimit(
            this.subFeature,
            StateObserver.now(),
          );
          if (checked && selectionCount >= limit) {
            // We've hit the selection limit, so clear the checkbox and return
            checkbox.setChecked(false);
            return;
          }

          if (checked) {
            sounds.sfx.play('ok');
            this.selectedFriends.set(friend, true);
            this.updateSelectionCounter(selectionCount + 1);
          } else {
            sounds.sfx.play('cancel');
            this.selectedFriends.set(friend, false);
            this.updateSelectionCounter(selectionCount - 1);
          }
        },
      });
      container.addSubview(checkbox.view);
      getFlexNode(checkbox.view).updateStyle({
        marginLeft: 10,
        marginRight: 6,
      });

      onClick = async () => checkbox.toggle();
    } else {
      container.flex.updateStyle({ paddingLeft: 10 });
      onClick = async () => this.confirmInvite(friend);
    }

    const friendTile = new FriendTile({
      subFeature: this.subFeature,
      friend,
      onClick,
    });
    container.addSubview(friendTile.view);

    return container;
  }

  private filterFriends(searchValue: string) {
    let matchedFriends = this.friends;
    if (searchValue) {
      const locale = getLocale(StateObserver.getState().user);
      const upperCaseValue = searchValue.toLocaleUpperCase(locale);
      matchedFriends = matchedFriends.filter(({ name }) =>
        name.toLocaleUpperCase(locale).includes(upperCaseValue),
      );
    }
    this.friendsList.setItems(
      matchedFriends.slice(0, Social.ruleset.inviteFriendListLimit),
    );
  }

  private getSelectedFriends() {
    return Array.from(this.selectedFriends.keys()).filter((friend) =>
      this.selectedFriends.get(friend),
    );
  }

  private updateSelectionCounter(selectedCount: number) {
    if (!this.selectionCounter) {
      return;
    }

    const { user } = StateObserver.getState();
    const limit = Social.getters(user).getInviteLimit(
      this.subFeature,
      StateObserver.now(),
    );
    this.selectionCounter.localeText = () => `${selectedCount}/${limit}`;
  }

  private async confirmInvite(...friends: SocialFriend[]) {
    if (!friends.length) {
      return;
    }

    this.close();

    if (friends.length > 1 && this.isMultiSelectEnabled()) {
      const avatars = friends.map((x) => x.photo);
      if (await openPopupPromise('popupConfirmInviteMulti', { avatars })) {
        await this.inviteFriends(...friends.map((x) => x.id));
        friends.forEach((friend) =>
          this.friends.splice(this.friends.indexOf(friend), 1),
        );
      }
    } else {
      if (friends.length > 1) {
        captureMessage(
          'Attempting to invite multiple friends when not in enabled bucket',
          'warning',
        );
      }

      const friend = friends[0];
      if (await openPopupPromise('popupConfirmInvite', friend)) {
        await this.inviteFriends(friend.id);
        this.friends.splice(this.friends.indexOf(friend), 1);
      }
    }

    await openPopupPromise('popupInviteFriends', {
      friends: this.friends,
      subFeature: this.subFeature,
    });
  }

  private async inviteFriends(...friendIds: string[]) {
    try {
      const receiverIDs = await inviteAsync({
        receiverIDs: friendIds,
        confirmPopup: false,
        creativeAsset: await getRandomShareInviteCreative(),
        creativeText: getCreativeText('invite'),
        data: {
          feature: Feature.INVITE._,
          $subFeature: this.subFeature,
        },
      });

      if (receiverIDs?.length) {
        await openPopupPromise('popupInviteReward', {
          inviteCount: receiverIDs.length,
          subFeature: this.subFeature,
        });
        await StateObserver.invoke.claimInviteRewards(this.subFeature);
      }
    } catch (e) {
      // USER_INPUT error handled by platform
      if (e.code === 'USER_INPUT') {
        // Line throws USER_INPUT, dont show it again
      }
    }
  }
}
